import { useState, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FolderIcon,
  FileTextIcon,
  UploadIcon,
  FolderPlusIcon,
  Trash2Icon,
  DownloadIcon,
  RefreshCwIcon,
  ChevronRightIcon,
  HomeIcon,
  AlertCircleIcon,
  XIcon,
} from 'lucide-react'
import { apiClient, SOTFileEntry, SOTFolderEntry } from '../lib/api'

interface SOTFileBrowserProps {
  subdomain: string
}

const QUICK_ACCESS_FOLDERS = [
  { label: 'Crawl Documents', path: 'crawl/md/documents' },
  { label: 'Crawl Services', path: 'crawl/md/services' },
  { label: 'Other Documents', path: 'other/md/documents' },
  { label: 'Other Services', path: 'other/md/services' },
  { label: 'Audit Report', path: 'doc-prep-raw/audit-report' },
  { label: 'Competitors Crawl', path: 'doc-prep-raw/competitors-crawl' },
  { label: 'Questionnaire', path: 'doc-prep-raw/questionaire' },
]

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function pathSegments(path: string): { name: string; path: string }[] {
  if (!path) return []
  const parts = path.split('/').filter(Boolean)
  return parts.map((name, i) => ({
    name,
    path: parts.slice(0, i + 1).join('/'),
  }))
}

export default function SOTFileBrowser({ subdomain }: SOTFileBrowserProps) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [currentPath, setCurrentPath] = useState('')
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set())
  const [showNewFolderInput, setShowNewFolderInput] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [uploading, setUploading] = useState(false)

  const queryKey = ['sot-files', subdomain, currentPath]

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => apiClient.listSOTFiles(subdomain, currentPath),
    retry: 1,
  })

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) => apiClient.uploadSOTFiles(subdomain, currentPath, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sot-files', subdomain] })
      setUploading(false)
    },
    onError: () => setUploading(false),
  })

  const deleteMutation = useMutation({
    mutationFn: (paths: string[]) => {
      const relativePaths = paths.map((p) => {
        const prefix = subdomain + '/'
        return p.startsWith(prefix) ? p.slice(prefix.length) : p
      })
      return apiClient.deleteSOTFiles(subdomain, relativePaths)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sot-files', subdomain] })
      setSelectedPaths(new Set())
      setShowDeleteConfirm(false)
    },
  })

  const createFolderMutation = useMutation({
    mutationFn: (name: string) => apiClient.createSOTFolder(subdomain, currentPath, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sot-files', subdomain] })
      setShowNewFolderInput(false)
      setNewFolderName('')
    },
  })

  const navigateTo = useCallback((path: string) => {
    setCurrentPath(path)
    setSelectedPaths(new Set())
  }, [])

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (!files || files.length === 0) return
      setUploading(true)
      uploadMutation.mutate(Array.from(files))
      e.target.value = ''
    },
    [uploadMutation],
  )

  const toggleSelection = useCallback((path: string) => {
    setSelectedPaths((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    if (!data) return
    const allPaths = [
      ...data.folders.map((f) => f.path),
      ...data.files.map((f) => f.path),
    ]
    if (selectedPaths.size === allPaths.length) {
      setSelectedPaths(new Set())
    } else {
      setSelectedPaths(new Set(allPaths))
    }
  }, [data, selectedPaths.size])

  const handleDeleteSelected = useCallback(() => {
    if (selectedPaths.size === 0) return
    setShowDeleteConfirm(true)
  }, [selectedPaths.size])

  const confirmDelete = useCallback(() => {
    deleteMutation.mutate(Array.from(selectedPaths))
  }, [deleteMutation, selectedPaths])

  const handleCreateFolder = useCallback(() => {
    const name = newFolderName.trim()
    if (!name) return
    createFolderMutation.mutate(name)
  }, [createFolderMutation, newFolderName])

  const segments = pathSegments(currentPath)
  const folders: SOTFolderEntry[] = data?.folders ?? []
  const files: SOTFileEntry[] = data?.files ?? []
  const totalItems = folders.length + files.length

  return (
    <div>
      {/* Quick access */}
      <div className="mb-4">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Quick access</p>
        <div className="flex flex-wrap gap-2">
          {QUICK_ACCESS_FOLDERS.map((qa) => (
            <button
              key={qa.path}
              onClick={() => navigateTo(qa.path)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                currentPath === qa.path
                  ? 'bg-primary-50 border-primary-200 text-primary-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FolderIcon className="h-3 w-3" />
              {qa.label}
            </button>
          ))}
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-sm text-gray-500 mb-4 bg-white border border-gray-100 rounded-lg px-4 py-2.5">
        <button
          onClick={() => navigateTo('')}
          className="flex items-center gap-1 hover:text-primary-600 transition-colors font-medium"
        >
          <HomeIcon className="h-3.5 w-3.5" />
          {subdomain}
        </button>
        {segments.map((seg) => (
          <span key={seg.path} className="flex items-center gap-1">
            <ChevronRightIcon className="h-3.5 w-3.5 text-gray-300" />
            <button
              onClick={() => navigateTo(seg.path)}
              className="hover:text-primary-600 transition-colors font-medium"
            >
              {seg.name}
            </button>
          </span>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            <UploadIcon className="h-4 w-4" />
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".md,.json,.txt,.pdf,.xlsx,.xls,.csv,.xml,.html"
            className="hidden"
            onChange={handleFileUpload}
          />

          <button
            onClick={() => {
              setShowNewFolderInput(true)
              setNewFolderName('')
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FolderPlusIcon className="h-4 w-4" />
            New folder
          </button>

          {selectedPaths.size > 0 && (
            <button
              onClick={handleDeleteSelected}
              disabled={deleteMutation.isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
            >
              <Trash2Icon className="h-4 w-4" />
              Delete ({selectedPaths.size})
            </button>
          )}
        </div>

        <button
          onClick={() => queryClient.invalidateQueries({ queryKey })}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
        >
          <RefreshCwIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* New folder inline input */}
      {showNewFolderInput && (
        <div className="flex items-center gap-2 mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <FolderPlusIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFolder()
              if (e.key === 'Escape') setShowNewFolderInput(false)
            }}
            placeholder="Folder name"
            className="flex-1 text-sm bg-white border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-primary-400"
            autoFocus
          />
          <button
            onClick={handleCreateFolder}
            disabled={!newFolderName.trim() || createFolderMutation.isLoading}
            className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
          >
            Create
          </button>
          <button
            onClick={() => setShowNewFolderInput(false)}
            className="p-1.5 text-gray-400 hover:text-gray-600"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Upload errors */}
      {uploadMutation.data?.errors && uploadMutation.data.errors.length > 0 && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          <p className="font-medium mb-1">Some files failed to upload:</p>
          <ul className="list-disc list-inside">
            {uploadMutation.data.errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Error state */}
      {!!error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-8 text-center">
          <AlertCircleIcon className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-sm text-red-700">
            Failed to load files. Make sure the SOT storage is configured.
          </p>
        </div>
      )}

      {/* Loading state */}
      {isLoading && !error && (
        <div className="bg-white border border-gray-100 rounded-xl px-6 py-12 text-center">
          <RefreshCwIcon className="h-6 w-6 text-gray-400 mx-auto mb-2 animate-spin" />
          <p className="text-sm text-gray-500">Loading files...</p>
        </div>
      )}

      {/* File table */}
      {!isLoading && !error && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          {totalItems === 0 ? (
            <div className="px-6 py-12 text-center">
              <FolderIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 mb-1">This folder is empty</p>
              <p className="text-xs text-gray-400">Upload files or create a folder to get started</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 text-left">
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedPaths.size === totalItems && totalItems > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Name
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">
                    Size
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-48">
                    Last Modified
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-24 text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {folders.map((folder) => (
                  <tr
                    key={folder.path}
                    className="border-b border-gray-50 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedPaths.has(folder.path)}
                        onChange={() => toggleSelection(folder.path)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => {
                          const prefix = subdomain + '/'
                          const relative = folder.path.startsWith(prefix)
                            ? folder.path.slice(prefix.length)
                            : folder.path
                          navigateTo(relative.replace(/\/$/, ''))
                        }}
                        className="inline-flex items-center gap-2 text-sm font-medium text-gray-900 hover:text-primary-600 transition-colors"
                      >
                        <FolderIcon className="h-4 w-4 text-yellow-500" />
                        {folder.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">—</td>
                    <td className="px-4 py-3 text-sm text-gray-400">—</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => {
                          setSelectedPaths(new Set([folder.path]))
                          setShowDeleteConfirm(true)
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete folder"
                      >
                        <Trash2Icon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}

                {files.map((file) => (
                  <tr
                    key={file.path}
                    className="border-b border-gray-50 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedPaths.has(file.path)}
                        onChange={() => toggleSelection(file.path)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="inline-flex items-center gap-2 text-sm text-gray-900">
                        <FileTextIcon className="h-4 w-4 text-gray-400" />
                        {file.name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatBytes(file.size)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(file.last_modified)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => {
                            const prefix = subdomain + '/'
                            const relative = file.path.startsWith(prefix)
                              ? file.path.slice(prefix.length)
                              : file.path
                            apiClient.downloadSOTFile(subdomain, relative)
                          }}
                          className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"
                          title="Download"
                        >
                          <DownloadIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedPaths(new Set([file.path]))
                            setShowDeleteConfirm(true)
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <Trash2Icon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete items?</h3>
            <p className="text-sm text-gray-500 mb-4">
              This will permanently delete {selectedPaths.size} item(s). Folders and all their
              contents will be removed. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteMutation.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
